import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import type { BatchSourceLead, BatchSourceFilters } from '../../hooks/useBatchSource';

interface ResultsTableProps {
  leads: BatchSourceLead[];
  total: number;
  page: number;
  pages: number;
  isLoading: boolean;
  isFetching: boolean;
  filters: BatchSourceFilters;
  setFilters: (updates: Partial<BatchSourceFilters>) => void;
}

const COLUMNS = [
  { key: 'address', label: 'Address', sortable: true },
  { key: 'total_units', label: 'Units', sortable: true },
  { key: 'neighborhood_tier', label: 'Tier', sortable: true },
  { key: 'cap_rate', label: 'Cap Rate', sortable: true },
  { key: 'noi', label: 'NOI', sortable: true },
  { key: 'estimated_monthly_rent', label: 'Rent/mo', sortable: true },
  { key: 'mep_permit_count', label: 'Permits', sortable: false },
  { key: 'violation_count', label: 'Violations', sortable: false },
  { key: 'combined_score', label: 'Score', sortable: true },
] as const;

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number | null): string {
  if (value === null || value === undefined) return '--';
  return `${(value * 100).toFixed(1)}%`;
}

function tierBadgeColor(tier: string): string {
  switch (tier) {
    case 'Tier1':
      return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    case 'Tier2':
      return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'Tier3':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    default:
      return 'bg-slate-500/20 text-stone-400 border-slate-500/30';
  }
}

export const ResultsTable: React.FC<ResultsTableProps> = ({
  leads,
  total,
  page,
  pages,
  isLoading,
  isFetching,
  filters,
  setFilters,
}) => {
  const navigate = useNavigate();

  const handleSort = (column: string) => {
    if (filters.sortBy === column) {
      setFilters({ sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' });
    } else {
      setFilters({ sortBy: column, sortOrder: 'desc' });
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pages) {
      setFilters({ page: newPage });
    }
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (filters.sortBy !== column) return <ArrowUpDown className="w-3.5 h-3.5 text-stone-600" />;
    return filters.sortOrder === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-gold" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-gold" />
    );
  };

  const startItem = (page - 1) * filters.limit + 1;
  const endItem = Math.min(page * filters.limit, total);

  if (isLoading && leads.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-stone-500 text-sm">Loading properties...</p>
        </div>
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-stone-400 text-lg mb-1">No properties found</p>
          <p className="text-stone-500 text-sm">Try adjusting your filters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Count header */}
      <div className="px-6 py-3 border-b border-slate-700 flex items-center justify-between">
        <p className="text-sm text-stone-400">
          Showing <span className="text-ivory font-medium">{startItem}–{endItem}</span> of{' '}
          <span className="text-ivory font-medium">{total.toLocaleString()}</span> properties
          {isFetching && !isLoading && (
            <span className="ml-2 text-gold text-xs">(updating...)</span>
          )}
        </p>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-slate-800/50 sticky top-0 z-10">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  className={`px-4 py-3 text-left text-xs font-medium text-stone-400 uppercase tracking-wider ${
                    col.sortable ? 'cursor-pointer hover:text-ivory transition-colors' : ''
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    {col.label}
                    {col.sortable && <SortIcon column={col.key} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {leads.map((lead) => (
              <tr
                key={lead.id}
                onClick={() => navigate(`/property/${lead.id}`)}
                className="hover:bg-slate-800/50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-ivory truncate max-w-[260px]">
                      {lead.address}
                    </p>
                    {lead.neighborhood && (
                      <p className="text-xs text-stone-500">{lead.neighborhood}</p>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-ivory font-mono">
                  {lead.total_units}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${tierBadgeColor(lead.neighborhood_tier)}`}
                  >
                    {lead.neighborhood_tier}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-mono text-gold">
                  {formatPercent(lead.cap_rate)}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-ivory">
                  {formatCurrency(lead.noi)}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-ivory">
                  <div className="flex items-center gap-1.5">
                    {formatCurrency(lead.estimated_monthly_rent)}
                    {lead.rent_confidence && (
                      <span
                        className={`text-[10px] font-sans px-1 py-0.5 rounded ${
                          lead.rent_confidence === 'High'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : lead.rent_confidence === 'Medium'
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-red-500/20 text-red-400'
                        }`}
                        title={lead.rent_estimate_source || undefined}
                      >
                        {lead.rent_confidence}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-stone-300">
                  {lead.mep_permit_count ?? 0}
                  {lead.has_recent_mep && (
                    <span className="ml-1 text-xs text-amber-400">recent</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  {(lead.violation_count ?? 0) > 0 ? (
                    <span className="text-red-400">{lead.violation_count}</span>
                  ) : (
                    <span className="text-stone-500">0</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {lead.combined_score !== null ? (
                    <span
                      className={`text-sm font-mono font-medium ${
                        lead.combined_score >= 70
                          ? 'text-emerald-400'
                          : lead.combined_score >= 40
                            ? 'text-amber-400'
                            : 'text-stone-400'
                      }`}
                    >
                      {lead.combined_score.toFixed(0)}
                    </span>
                  ) : (
                    <span className="text-stone-500 text-sm">--</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="border-t border-slate-700 px-6 py-3 flex items-center justify-between">
          <p className="text-xs text-stone-500">
            Page {page} of {pages}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="p-1.5 rounded-lg border border-slate-600 text-stone-400 hover:text-ivory hover:border-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
              let pageNum: number;
              if (pages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= pages - 2) {
                pageNum = pages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                    pageNum === page
                      ? 'bg-gold text-slate-900'
                      : 'text-stone-400 hover:text-ivory hover:bg-slate-800'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= pages}
              className="p-1.5 rounded-lg border border-slate-600 text-stone-400 hover:text-ivory hover:border-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
